<script lang="ts" setup>
import {QBtn} from 'quasar';
import {computed} from 'vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';

const props = defineProps<{
  displayFlat?: boolean;
  type: 'fullscreen' | 'pause' | 'play' | 'playInitial' | 'skipBackward' | 'skipForward' | 'mute' | 'unmute' | 'speed';
  disable?: boolean;
}>();

const dense = computed((): boolean => {
  return props.displayFlat || !(props.type === 'play' || props.type === 'pause');
});

const labelLangKey = computed((): string => {
  return {
    fullscreen: 'recco_media_control_fullscreen',
    pause: 'recco_media_control_pause',
    play: 'recco_media_control_play',
    skipBackward: 'recco_media_control_little_back',
    skipForward: 'recco_media_control_little_forward',
    playInitial: 'recco_media_control_play',
    mute: 'mute',
    unmute: 'unmute',
    speed: 'playback_speed',
  }[props.type];
});

const iconName = computed((): string | null => {
  return {
    play: 'media-play.svg',
    playInitial: 'play.svg',
    pause: 'media-pause.svg',
    skipForward: 'media-forward.svg',
    skipBackward: 'media-backward.svg',
    fullscreen: 'media-expand.svg',
    mute: 'volume-max.svg',
    unmute: 'volume-x.svg',
    speed: null,
  }[props.type] ?? null;
});

const isPlayIcon = computed((): boolean => {
  return props.type === 'play' || props.type === 'playInitial';
});

</script>

<template>
  <q-btn
    round
    :flat="props.displayFlat"
    :dense="dense"
    :disable="disable"
    class="btn focus-visible"
    :class="`btn--${type}`"
  >
    <svg-illustration
      v-if="iconName"
      :name="iconName"
      :inner-class="`btn-icon ${isPlayIcon ? 'btn-icon--play' : ''}`"
    />
    <span v-if="type === 'speed'" class="speed-label">
      <slot/>
    </span>
    <span class="sr-only">
      {{ $t(labelLangKey) }}
    </span>
  </q-btn>
</template>

<style lang="scss" scoped>
// These controls always render on a dark video/audio surface,
// so scheme-pinned vars are needed to force white/dark regardless of theme.
.btn {
  @include focus-visible;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);

  :deep(.btn-icon) {
    width: 18px;
    height: 18px;

    @include screen-sm {
      width: 24px;
      height: 24px;
    }
  }

  &--play:not(.q-btn--flat),
  &--pause:not(.q-btn--flat) {
    background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  }

  &--playInitial.btn--playInitial {
    width: var(--Spacing-spacing-7);
    height: var(--Spacing-spacing-7);
    min-width: unset;
    min-height: unset;
    padding: 0;
    border-radius: 50%;
    background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);

    :deep(.btn-icon) {
      width: 14px;
      height: 14px;

      @include screen-sm {
        width: 16px;
        height: 16px;
      }
    }
  }

  &--mute,
  &--unmute {
    :deep(.btn-icon) {
      width: 16px;
      height: 16px;

      @include screen-sm {
        width: 20px;
        height: 20px;
      }
    }
  }

  &--speed {
    min-width: auto;
    min-height: auto;
    padding: 4px 8px;
    border-radius: 100px;
  }
}

.speed-label {
  @include font-caption-sm;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
  white-space: nowrap;
}
</style>
